import { consolidarServiciosGuia } from "./consolidarServiciosGuiaHelpers";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { Mercancia } from "../../entities/solicitudes/Mercancia";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { enums } from "../../enums/enums";

export type GuiaRecalcularTarifaRequest = {
    sucursalRemitenteId: number;
    sucursalDestinatarioId: number;
    areaServicioRemitenteId: number;
    areaServicioDestinatarioId: number;
    remitenteId: number;
    destinatarioId: number;
    paisOrigenId: number;
    paisDestinoId: number;
    tipoServicioId: number;
    tipoEnvioId: number;
    esRad: number;
    esEad: number;
    esRadCita: number;
    esEadCita: number;
    esRadCitaCedis: number;
    esEadCitaCedis: number;
    tipoSeguroId: number;
    valorMercancia: number;
    cobroEnDestino: number;
    unidadId: number;
    unidadMercanciaId: number;
    claseId: number;
    cruceInternacionalId: number;
    distribuidorLogisticoId: number;
    esIntra: number;
    proyectoId: number;
    grupoProyectoId: number;
    idioma: string;
    mercancias: Array<{
        cantidad: number;
        embalajeId: number;
        peso: number;
        largo: number;
        ancho: number;
        alto: number;
        altoReal?: number | null;
        esNoEstibable: number;
        skuId: number;
    }>;
    conceptos: Array<{
        conceptoId: number;
        esOrigen: number;
        esDestino: number;
        cantidadOrigen: number;
        cantidadDestino: number;
        cantidad: number;
        costo: number;
        tipoCostoId: number;
    }>;
};

type BuildArgs = {
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    guiaMercancias: Mercancia[];
    guiaServicios: ServicioAdicional[];
    idioma?: string;
};

const asInt = (value: any, fallback = 0): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const asDecimal = (value: any, fallback = 0): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

export function buildGuiaRecalcularTarifaRequest(args: BuildArgs): GuiaRecalcularTarifaRequest {
    const { guiaWizard, guiaOrigen, guiaDestino, guiaMercancias, guiaServicios } = args;
    const consolidado = consolidarServiciosGuia(
        guiaOrigen?.serviciosAdicionales ?? [],
        guiaDestino?.serviciosAdicionales ?? [],
        guiaServicios ?? []
    );

    const conceptos = consolidado.servicios.map(s => ({
        conceptoId: asInt(s.id),
        esOrigen: s.aplicaOrigen ? 1 : 0,
        esDestino: s.aplicaDestino ? 1 : 0,
        cantidadOrigen: asInt(s.cantidadOrigen),
        cantidadDestino: asInt(s.cantidadDestino),
        cantidad: asInt(s.cantidad),
        costo: asDecimal(s.costo),
        tipoCostoId: asInt(s.tipoCostoID)
    }));

    const idsConcepto = new Set(conceptos.map(c => c.conceptoId));
    const esRadCita = idsConcepto.has(9) || idsConcepto.has(47) ? 1 : 0;
    const esEadCita = idsConcepto.has(12) || idsConcepto.has(6) ? 1 : 0;

    const tipoSeguroId = guiaWizard.esSeguroBasico
        ? 1
        : (guiaWizard.esSeguroMercancia ? 2 : 0);

    const mercancias = (guiaMercancias ?? [])
        .filter(m => asInt(m.cantidad) > 0 && asInt(m.embalajeId) > 0)
        .map(m => ({
            cantidad: asInt(m.cantidad),
            embalajeId: asInt(m.embalajeId),
            peso: asDecimal(m.peso),
            largo: asDecimal(m.largo),
            ancho: asDecimal(m.ancho),
            alto: asDecimal(m.alto),
            altoReal: m.altoReal != null ? asDecimal(m.altoReal) : asDecimal(m.alto),
            esNoEstibable: m.esEstibable ? 1 : 0,
            skuId: asInt(m.skuID)
        }));

    return {
        sucursalRemitenteId: asInt(guiaOrigen?.sucursal?.id),
        sucursalDestinatarioId: asInt(guiaDestino?.sucursal?.id),
        areaServicioRemitenteId: asInt(guiaOrigen?.areaServicio?.id),
        areaServicioDestinatarioId: asInt(guiaDestino?.areaServicio?.id),
        remitenteId: asInt(guiaWizard.remitenteId),
        destinatarioId: asInt(guiaWizard.destinatarioId),
        paisOrigenId: asInt(guiaOrigen?.paisId),
        paisDestinoId: asInt(guiaDestino?.paisId),
        tipoServicioId: asInt(guiaWizard.tipoServicioId),
        tipoEnvioId: asInt(guiaWizard.tipoEnvioId),
        esRad: guiaOrigen?.tipoRecoleccionId === enums.tipoRecoleccion.ocurre ? 0 : 1,
        esEad: asInt(guiaWizard.esEad),
        esRadCita,
        esEadCita,
        esRadCitaCedis: guiaOrigen?.EsCitaRadCedis ? 1 : 0,
        esEadCitaCedis: guiaDestino?.EsCitaEadCedis ? 1 : 0,
        tipoSeguroId,
        valorMercancia: asDecimal(guiaWizard.valorMercancia),
        cobroEnDestino: 0,
        unidadId: asInt(guiaWizard.unidadMedidaPesoId),
        unidadMercanciaId: asInt(guiaWizard.unidadMedidaLongitudId),
        claseId: 0,
        cruceInternacionalId: 0,
        distribuidorLogisticoId: 0,
        esIntra: 0,
        proyectoId: 0,
        grupoProyectoId: 0,
        idioma: args.idioma || localStorage.getItem("idioma") || "es-MX",
        mercancias,
        conceptos
    };
}
